/**
 * Eine Rechnung im Einzelnen.
 *
 * Der Weg ist vorgezeichnet: Entwurf → gestellt → bezahlt. Jeder Schritt
 * hat Folgen, die hier benannt werden, statt sie nur auszulösen:
 *
 *   * Beim Stellen entstehen Nummer, PDF und der Einnahme-Beleg. Ab dann ist
 *     die Rechnung unveränderlich — eine gestellte Rechnung ist beim Kunden.
 *   * Bei der Zahlung wird der Beleg gebucht und die Einnahme für die EÜR
 *     wirksam. Maßgeblich ist das Zahlungsdatum, nicht das Rechnungsdatum.
 */
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Dialog } from '../bausteine/Dialog';
import { Betragsfeld, Textbereich, Textfeld } from '../bausteine/Feld';
import { Seite } from '../bausteine/Layout';
import { Marker } from '../bausteine/Marker';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import { Laedt } from '../bausteine/Zustand';
import { useAbruf, useVorgang } from '../lib/abruf';
import { api } from '../lib/api';
import { datum, heute, prozent, zahl } from '../lib/format';
import { euro } from '../lib/geld';
import { useSitzung } from '../sitzung/SitzungKontext';
import type { Rechnung } from '../lib/typen';

export function RechnungDetail(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const navigiere = useNavigate();
  const { darfSchreiben } = useSitzung();

  const abruf = useAbruf<Rechnung>(id ? `/api/rechnungen/${id}` : null);
  const vorgang = useVorgang();

  const [zahlungOffen, setzeZahlungOffen] = useState(false);
  const [zahlungsdatum, setzeZahlungsdatum] = useState(heute());
  const [teilbetrag, setzeTeilbetrag] = useState('');
  const [stornoOffen, setzeStornoOffen] = useState(false);
  const [stornoGrund, setzeStornoGrund] = useState('');

  if (abruf.fehler) {
    return (
      <Seite titel="Rechnung">
        <Fehlermeldung text={abruf.fehler} />
        <p style={{ marginTop: '1rem' }}>
          <Link to="/rechnungen">Zurück zur Rechnungsliste</Link>
        </p>
      </Seite>
    );
  }

  if (!abruf.daten) {
    return (
      <Seite titel="Rechnung">
        <Laedt />
      </Seite>
    );
  }

  const r = abruf.daten;
  const istEntwurf = r.status === 'entwurf';
  const nimmtZahlung = r.status === 'gestellt' || r.status === 'teilbezahlt';
  const ueberfaellig = nimmtZahlung && r.faelligAm !== null && r.faelligAm < heute();

  async function stellen(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden(`/api/rechnungen/${r.id}/stellen`),
    );
    if (ergebnis !== undefined) abruf.neuLaden();
  }

  async function zahlungMelden(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden(`/api/rechnungen/${r.id}/bezahlt`, {
        zahlungsdatum,
        ...(teilbetrag.trim() ? { betrag: teilbetrag } : {}),
      }),
    );
    if (ergebnis !== undefined) {
      setzeZahlungOffen(false);
      setzeTeilbetrag('');
      abruf.neuLaden();
    }
  }

  async function verwerfen(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() => api.entfernen(`/api/rechnungen/${r.id}`));
    if (ergebnis !== undefined) navigiere('/rechnungen');
  }

  async function stornieren(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden(`/api/rechnungen/${r.id}/stornieren`, { grund: stornoGrund.trim() }),
    );
    if (ergebnis !== undefined) {
      setzeStornoOffen(false);
      setzeStornoGrund('');
      abruf.neuLaden();
    }
  }

  return (
    <Seite
      titel={r.nummer ?? 'Rechnungsentwurf'}
      untertitel={r.betreff ?? undefined}
      aktionen={
        <>
          {r.status !== 'entwurf' ? (
            <a
              className="knopf"
              href={`/api/rechnungen/${r.id}/pdf`}
              target="_blank"
              rel="noreferrer"
            >
              PDF
            </a>
          ) : null}
          {darfSchreiben && istEntwurf ? (
            <>
              <button
                type="button"
                className="knopf"
                onClick={() => navigiere(`/rechnungen/${r.id}/aendern`)}
              >
                Ändern
              </button>
              <button
                type="button"
                className="knopf haupt"
                onClick={() => void stellen()}
                disabled={vorgang.laeuft}
              >
                {vorgang.laeuft ? 'Wird gestellt …' : 'Rechnung stellen'}
              </button>
            </>
          ) : null}
          {darfSchreiben && nimmtZahlung ? (
            <button type="button" className="knopf haupt" onClick={() => setzeZahlungOffen(true)}>
              Zahlung melden
            </button>
          ) : null}
          {/* Ein Entwurf wird verworfen, eine gestellte Rechnung storniert:
              Sie hat eine Nummer und liegt beim Empfänger. */}
          {darfSchreiben && istEntwurf ? (
            <button
              type="button"
              className="knopf gefahr"
              onClick={() => void verwerfen()}
              disabled={vorgang.laeuft}
            >
              Verwerfen
            </button>
          ) : null}
          {darfSchreiben && !istEntwurf && r.status !== 'storniert' && r.status !== 'bezahlt' ? (
            <button type="button" className="knopf gefahr" onClick={() => setzeStornoOffen(true)}>
              Stornieren
            </button>
          ) : null}
        </>
      }
    >
      <div className="stapel" style={{ maxWidth: '1100px' }}>
        <Fehlermeldung text={vorgang.fehler} />

        {istEntwurf ? (
          <Meldung ton="info" titel="Entwurf.">
            Noch keine Nummer, noch kein PDF. Beim Stellen entsteht beides —
            zusammen mit dem Einnahme-Beleg. Danach ist die Rechnung
            unveränderlich.
          </Meldung>
        ) : null}

        {ueberfaellig ? (
          <Meldung ton="gefahr" titel="Überfällig.">
            Fällig war {datum(r.faelligAm)}. Das sind{' '}
            {zahl(
              Math.floor(
                (Date.parse(heute()) - Date.parse(r.faelligAm ?? heute())) / 86_400_000,
              ),
              0,
            )}{' '}
            Tage.
          </Meldung>
        ) : null}

        {r.status === 'storniert' ? (
          <Meldung ton="gefahr" titel="Storniert.">
            Die Nummer bleibt vergeben — eine Lücke in der Nummernfolge wäre
            ein Mangel. Der Vorgang bleibt nachvollziehbar erhalten.
          </Meldung>
        ) : null}

        <div className="raster zwei">
          <div className="karte">
            <div className="karte-kopf">
              <h2>Rechnung</h2>
              <Marker status={ueberfaellig ? 'ueberfaellig' : r.status} />
            </div>
            <div className="karte-koerper">
              <dl className="paare">
                <dt>Nummer</dt>
                <dd className="fest">{r.nummer ?? <span className="still">noch keine</span>}</dd>

                <dt>Rechnungsdatum</dt>
                <dd>{datum(r.rechnungsdatum)}</dd>

                <dt>Fällig</dt>
                <dd>{datum(r.faelligAm)}</dd>

                {r.leistungVon || r.leistungBis ? (
                  <>
                    <dt>Leistungszeitraum</dt>
                    <dd>
                      {datum(r.leistungVon)} – {datum(r.leistungBis)}
                    </dd>
                  </>
                ) : null}

                {r.bezahltAm ? (
                  <>
                    <dt>Bezahlt am</dt>
                    <dd>{datum(r.bezahltAm)}</dd>
                  </>
                ) : null}

                {r.belegId ? (
                  <>
                    <dt>Beleg</dt>
                    <dd>
                      <Link to={`/belege/${r.belegId}`}>Einnahme-Beleg ansehen</Link>
                    </dd>
                  </>
                ) : null}
              </dl>
            </div>
          </div>

          <div className="karte">
            <div className="karte-kopf">
              <h2>Empfänger</h2>
            </div>
            <div className="karte-koerper">
              {/* Nach dem Stellen gilt die eingefrorene Anschrift, nicht
                  die aktuelle des Kontakts: Zieht der Kunde um, muss die
                  Rechnung zeigen, was damals auf ihr stand. */}
              {r.empfaengerName ? (
                <address style={{ fontStyle: 'normal', whiteSpace: 'pre-line' }}>
                  {r.empfaengerName}
                  {r.empfaengerAnschrift ? `\n${r.empfaengerAnschrift}` : ''}
                </address>
              ) : r.kontakt ? (
                <address style={{ fontStyle: 'normal' }}>
                  {r.kontakt.name}
                  <br />
                  {r.kontakt.strasse}
                  <br />
                  {r.kontakt.plz} {r.kontakt.ort}
                </address>
              ) : (
                <span className="still">unbekannt</span>
              )}
              {r.empfaengerName ? (
                <p className="klein still" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                  Anschrift beim Stellen festgehalten.
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="karte">
          <div className="karte-kopf">
            <h2>Positionen</h2>
            {darfSchreiben && istEntwurf ? (
              <Link to={`/rechnungen/${r.id}/aendern`} className="knopf klein">
                Positionen ändern
              </Link>
            ) : null}
          </div>
          <div className="tabelle-rahmen">
            <table className="tabelle">
              <thead>
                <tr>
                  <th className="schmal">#</th>
                  <th>Bezeichnung</th>
                  <th className="zahl schmal">Menge</th>
                  <th className="zahl schmal">Einzelpreis</th>
                  {r.positionen.some((p) => Number.parseFloat(p.rabattProzent ?? '0') > 0) ? (
                    <th className="zahl schmal">Rabatt</th>
                  ) : null}
                  {!r.kleinunternehmerHinweis ? <th className="zahl schmal">Steuer</th> : null}
                  <th className="zahl">Summe</th>
                </tr>
              </thead>
              <tbody>
                {r.positionen.map((p) => (
                  <tr key={p.id ?? p.position}>
                    <td className="schmal still">{p.position}</td>
                    <td>
                      <span className="haupttext">{p.bezeichnung}</span>
                      {p.beschreibung ? <div className="nebentext">{p.beschreibung}</div> : null}
                    </td>
                    <td className="zahl schmal">
                      {zahl(p.menge, Number.isInteger(Number.parseFloat(p.menge)) ? 0 : 2)}{' '}
                      <span className="still klein">{p.einheit}</span>
                    </td>
                    <td className="zahl schmal">{euro(p.einzelpreis, false)}</td>
                    {r.positionen.some((x) => Number.parseFloat(x.rabattProzent ?? '0') > 0) ? (
                      <td className="zahl schmal">
                        {Number.parseFloat(p.rabattProzent ?? '0') > 0
                          ? prozent(p.rabattProzent)
                          : '—'}
                      </td>
                    ) : null}
                    {!r.kleinunternehmerHinweis ? (
                      <td className="zahl schmal">{prozent(p.steuersatz)}</td>
                    ) : null}
                    <td className="zahl">{euro(p.betragBrutto, false)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {!r.kleinunternehmerHinweis ? (
                  <>
                    <tr>
                      <td colSpan={r.kleinunternehmerHinweis ? 4 : 5}>Netto</td>
                      <td className="zahl">{euro(r.betragNetto, false)}</td>
                    </tr>
                    <tr>
                      <td colSpan={5}>Umsatzsteuer</td>
                      <td className="zahl">{euro(r.steuerbetrag, false)}</td>
                    </tr>
                  </>
                ) : null}
                <tr>
                  <td colSpan={r.kleinunternehmerHinweis ? 4 : 5}>
                    <strong>Gesamtbetrag</strong>
                  </td>
                  <td className="zahl">
                    <strong>{euro(r.betragBrutto)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {r.kleinunternehmerHinweis ? (
            <div className="karte-fuss klein still">
              Gemäß § 19 UStG wird keine Umsatzsteuer berechnet — der Hinweis
              steht auf dem PDF.
            </div>
          ) : null}
        </div>
      </div>

      {zahlungOffen ? (
        <Dialog
          titel="Zahlung melden"
          schliessen={() => setzeZahlungOffen(false)}
          fuss={
            <>
              <button type="button" className="knopf" onClick={() => setzeZahlungOffen(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="knopf haupt"
                disabled={zahlungsdatum === '' || vorgang.laeuft}
                onClick={() => void zahlungMelden()}
              >
                Zahlung buchen
              </button>
            </>
          }
        >
          <div className="stapel">
            <Meldung ton="info">
              Mit dem Zahlungsdatum wird der Einnahme-Beleg gebucht und die
              Einnahme in der EÜR wirksam — im Geschäftsjahr des Zuflusses,
              nicht dem der Rechnung.
            </Meldung>
            <div className="formularraster">
              <Textfeld
                beschriftung="Zahlungsdatum"
                art="date"
                wert={zahlungsdatum}
                setzeWert={setzeZahlungsdatum}
                pflicht
                hinweis="Wertstellung auf dem Konto"
              />
              <Betragsfeld
                beschriftung="Betrag"
                wert={teilbetrag}
                setzeWert={setzeTeilbetrag}
                hinweis={`leer = vollständig (${euro(r.betragBrutto)})`}
              />
            </div>
          </div>
        </Dialog>
      ) : null}

      {stornoOffen ? (
        <Dialog
          titel="Rechnung stornieren"
          schliessen={() => setzeStornoOffen(false)}
          fuss={
            <>
              <button type="button" className="knopf" onClick={() => setzeStornoOffen(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="knopf gefahr"
                disabled={stornoGrund.trim().length < 3 || vorgang.laeuft}
                onClick={() => void stornieren()}
              >
                Stornieren
              </button>
            </>
          }
        >
          <div className="stapel">
            <Meldung ton="warnung">
              {r.nummer
                ? `Die Nummer ${r.nummer} bleibt vergeben — Rechnungsnummern müssen lückenlos sein. `
                : ''}
              Der Vorgang bleibt erhalten und zählt in keiner Auswertung mehr
              mit.
            </Meldung>
            <Textbereich
              beschriftung="Grund"
              wert={stornoGrund}
              setzeWert={setzeStornoGrund}
              pflicht
              zeilen={3}
              platzhalter="Falscher Empfänger, Leistung nicht erbracht, Betrag falsch …"
              hinweis="Geht ins Journal."
            />
          </div>
        </Dialog>
      ) : null}
    </Seite>
  );
}
